/**
 * Adding to the presenter photo library from the client (F-134): the same
 * hook behind the thumbnail panel's "Add photos", the library dialog and
 * the /admin section, so every surface uploads and retries the same way.
 *
 * WHAT THE CLIENT DOES, AND DOES NOT. It uploads one file at a time (the
 * server tags each photo before it answers, so the description is normally
 * in the reply) and refreshes the shared store after each so the panel's
 * face fan grows as photos land. It does NOT run the tagging pass itself:
 * a photo that came back untagged shows the server's reason, and "Tag"
 * asks the server to run the same pass again. Retries are serialised —
 * one vision call at a time keeps the provider quota and the page calm.
 */
import { useCallback, useRef, useState } from "react";

import { tagPresenterPhoto, uploadPresenterPhoto } from "../../core/api.js";
import { showToast } from "../../ui/toast.js";
import { optimizeForUpload } from "../elements/optimizeImage.js";
import { presenterPhotoErrorMessage, tagErrorMessage } from "./errors.js";
import { loadPresenterPhotos } from "./load.js";

export interface LibraryUploads {
  /** Files still going through the upload, or null when none are. */
  adding: { done: number; total: number } | null;
  /** Photos whose tagging retry is in flight. */
  tagging: Readonly<Record<string, true>>;
  /** Per photo: why its last tagging pass produced nothing. */
  errors: Readonly<Record<string, string>>;
  addFiles(files: readonly File[]): Promise<void>;
  retryTag(id: string): Promise<void>;
}

export function useLibraryUploads(): LibraryUploads {
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);
  const [tagging, setTagging] = useState<Record<string, true>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const queue = useRef<Promise<void>>(Promise.resolve());

  const setError = useCallback((id: string, message: string | null) => {
    setErrors((current) => {
      const next = { ...current };
      if (message === null) delete next[id];
      else next[id] = message;
      return next;
    });
  }, []);

  const retryTag = useCallback(
    (id: string): Promise<void> => {
      const run = queue.current.then(async () => {
        setTagging((current) => ({ ...current, [id]: true }));
        setError(id, null);
        try {
          await tagPresenterPhoto(id);
          await loadPresenterPhotos();
        } catch (error) {
          setError(id, presenterPhotoErrorMessage(error));
        } finally {
          setTagging((current) => {
            const next = { ...current };
            delete next[id];
            return next;
          });
        }
      });
      queue.current = run;
      return run;
    },
    [setError],
  );

  const addFiles = useCallback(
    async (files: readonly File[]): Promise<void> => {
      if (files.length === 0) return;
      setAdding({ done: 0, total: files.length });
      try {
        for (const [index, file] of files.entries()) {
          try {
            // Same path as a board image drop — long edge capped and
            // re-encoded before it crosses the wire (F-30's optimizeForUpload).
            const { photo, tagError } = await uploadPresenterPhoto(await optimizeForUpload(file));
            if (tagError !== undefined) setError(photo.id, tagErrorMessage(tagError));
          } catch (error) {
            showToast(`${file.name}: ${presenterPhotoErrorMessage(error)}`);
          }
          setAdding({ done: index + 1, total: files.length });
          // Each photo shows up as it lands, not after the whole batch.
          await loadPresenterPhotos();
        }
      } finally {
        setAdding(null);
      }
    },
    [setError],
  );

  return { adding, tagging, errors, addFiles, retryTag };
}
